// src/pages/DivesitesDashboard.jsx
import React, { useState, useCallback, useEffect } from "react";
import { Container, CircularProgress, Box, Button, alpha } from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAuth } from "../contexts/AuthContext.js"; // Existing
import { DashboardProvider, useDashboard } from "../contexts/DashboardContext"; // NEW
import { useDashboardActions } from "../hooks/useDashboardActions.js"; // NEW
import { useDashboardData } from "../hooks/useDashboardData.js"; // NEW
import useDashboardApi from "../utils/dashboardApi.js"; // Only for specific loading/error states if needed
import { SYSTEM_LIMIT } from "../utils/constants.js"; // NEW

import DashboardHeader from "../components/divesitedashboard/DashboardHeader.js";
import DashboardTabs from "../components/divesitedashboard/DashboardTabs";
import DashboardControls from "../components/divesitedashboard/DashboardControls";
import DivesiteContent from "../components/divesitedashboard/DivesiteContent";
import PaginationControls from "../components/divesitedashboard/PaginationControls";
import SystemLimitChip from "../components/divesitedashboard/SystemLimitChip";
import AddDivesiteDialog from "../components/divesitedashboard/AddDivesiteDialog";

// Glassmorphic page container
const GlassPageContainer = styled(Container)(({ theme }) => ({
  position: "relative",
  minHeight: "100vh",
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(8),

  "&::before": {
    content: '""',
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 20%, 
      ${alpha(theme.palette.primary.main, 0.02)} 0%, 
      transparent 40%), 
      radial-gradient(circle at 80% 80%, 
      ${alpha(theme.palette.secondary.main, 0.02)} 0%, 
      transparent 40%), 
      radial-gradient(circle at 50% 50%, 
      ${alpha(theme.palette.background.paper, 0.03)} 0%, 
      transparent 60%)`,
    pointerEvents: "none",
    zIndex: -1,
  },
}));

// Enhanced back button with glassmorphic styling
const GlassBackButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.8)} 0%, 
    ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
  backdropFilter: "blur(15px) saturate(1.1)",
  WebkitBackdropFilter: "blur(15px) saturate(1.1)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  borderRadius: 12,
  padding: theme.spacing(1, 2),
  marginBottom: theme.spacing(3),
  marginLeft: theme.spacing(2),
  position: "relative",
  overflow: "hidden",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  textTransform: "none",
  fontWeight: 500,

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.4)}, 
      transparent)`,
    borderRadius: "12px 12px 0 0",
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.primary.main, 0.05)} 0%, 
      transparent 50%)`,
    opacity: 0,
    transition: "opacity 0.3s ease",
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 2,
  },

  "&:hover": {
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.background.paper, 0.9)} 0%, 
      ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
    borderColor: alpha(theme.palette.primary.main, 0.3),
    transform: "translateY(-2px)",
    boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,

    "&::after": {
      opacity: 1,
    },

    "& .MuiSvgIcon-root": {
      transform: "translateX(-2px)",
    },
  },

  "&:active": {
    transform: "translateY(-1px) scale(0.98)",
  },

  "& .MuiSvgIcon-root": {
    transition: "transform 0.3s ease",
  },

  [theme.breakpoints.down("sm")]: {
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    padding: theme.spacing(0.75, 1.5),
  },
}));

// Enhanced loading container
const GlassLoadingContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(4),
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.6)} 0%, 
    ${alpha(theme.palette.background.paper, 0.4)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
  borderRadius: 16,
  margin: theme.spacing(2),
  position: "relative",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.3)}, 
      transparent)`,
    borderRadius: "16px 16px 0 0",
  },

  "& .MuiCircularProgress-root": {
    position: "relative",
    zIndex: 1,
  },
}));

const DivesitesDashboardContent = ({ systemLimit }) => {
  const [tabValue, setTabValue] = useState(0);
  const [viewMode, setViewMode] = useState("grid");
  const [limit, setLimit] = useState(6);
  const [selectedDivesite, setSelectedDivesite] = useState(null);
  const [page, setPage] = useState(1);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showOnlyNonDashboard, setShowOnlyNonDashboard] = useState(false);

  // Using hooks from the new context and action hooks
  const {
    dashboardDivesiteIds,
    dashboardDivesites,
    availableSlots,
    likedDivesites,
    loadingLiked,
    isDeleting,
    refreshDashboard, // From DashboardContext
    fetchLikedDivesites, // From DashboardContext
  } = useDashboard();
  const { handleAddDivesite, handleRemoveDivesite } = useDashboardActions();

  // Dashboard API hooks for loading/error specific to the getDashboard call, if still needed.
  // Note: dashboardLoading and dashboardError are now implicitly handled by the context's state updates
  // or could be explicitly returned from the context if a loading spinner is needed specifically for dashboard fetch.
  const { dashloading: dashboardLoading, dasherror: dashboardError } =
    useDashboardApi();

  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);

  const handleDivesiteClick = useCallback((divesite) => {
    setSelectedDivesite(divesite);
  }, []);

  const handleBackToDashboard = useCallback(() => {
    setSelectedDivesite(null);
  }, []);

  const handleToggleViewMode = useCallback((newViewMode) => {
    setViewMode(newViewMode);
  }, []);

  const handlePageChange = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleOpenAddDialog = useCallback(() => {
    fetchLikedDivesites(); // Refresh liked divesites before opening
    setShowAddDialog(true);
  }, [fetchLikedDivesites]);

  const handleCloseAddDialog = useCallback(() => {
    setShowAddDialog(false);
  }, []);

  const handleToggleShowOnlyNonDashboard = useCallback(() => {
    setShowOnlyNonDashboard((prev) => !prev);
  }, []);

  // Filter liked divesites that aren't in the dashboard
  const filteredLikedDivesites = useCallback(() => {
    if (!likedDivesites || !dashboardDivesiteIds) {
      return [];
    }
    const dashboardIds = dashboardDivesiteIds; // This is already an array of IDs from the context
    let filtered = likedDivesites;
    if (showOnlyNonDashboard) {
      filtered = likedDivesites.filter(
        (site) => !dashboardIds.includes(site._id)
      );
    }
    return filtered;
  }, [likedDivesites, dashboardDivesiteIds, showOnlyNonDashboard]);

  // Reset page when limit or dashboard divesites change
  useEffect(() => {
    setPage(1);
  }, [limit, dashboardDivesiteIds]);

  const paginatedDivesites = dashboardDivesites.slice(
    (page - 1) * limit,
    page * limit
  );
  const totalDivesites = dashboardDivesites.length;
  const totalPages = Math.ceil(totalDivesites / limit);

  if (selectedDivesite) {
    const DivesiteDetails = React.lazy(() =>
      import("../components/divesites/divesitedetails.js")
    );

    return (
      <GlassPageContainer maxWidth="lg">
        <GlassBackButton
          variant="outlined"
          color="primary"
          onClick={handleBackToDashboard}
          startIcon={<ArrowBackIcon />}
        >
          Back to Dashboard
        </GlassBackButton>
        <React.Suspense
          fallback={
            <GlassLoadingContainer>
              <CircularProgress />
            </GlassLoadingContainer>
          }
        >
          <DivesiteDetails divesite={selectedDivesite} />
        </React.Suspense>
      </GlassPageContainer>
    );
  }

  return (
    <GlassPageContainer maxWidth="lg">
      <DashboardHeader />

      <DashboardControls
        viewMode={viewMode}
        handleToggleViewMode={handleToggleViewMode}
        refresh={refreshDashboard} // Use the context's refresh
        limit={limit}
        setLimit={setLimit}
        availableSlots={availableSlots}
        onAddDivesite={handleOpenAddDialog}
      />

      <DashboardTabs tabValue={tabValue} handleTabChange={handleTabChange} />

      <DivesiteContent
        tabValue={tabValue}
        loading={dashboardLoading} // From useDashboardApi, if still needed for initial dashboard fetch
        error={dashboardError} // From useDashboardApi
        refresh={refreshDashboard}
        divesites={dashboardDivesites}
        paginatedDivesites={paginatedDivesites}
        viewMode={viewMode}
        handleDivesiteClick={handleDivesiteClick}
        onRemoveDivesite={handleRemoveDivesite}
        isDeleting={isDeleting}
        availableSlots={availableSlots}
        onAddDivesite={handleOpenAddDialog}
      />

      {totalDivesites > limit && (
        <PaginationControls
          page={page}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        />
      )}

      {systemLimit < limit && <SystemLimitChip systemLimit={systemLimit} />}

      <AddDivesiteDialog
        open={showAddDialog}
        onClose={handleCloseAddDialog}
        divesites={filteredLikedDivesites()}
        onAddDivesite={handleAddDivesite}
        loading={loadingLiked}
        showOnlyNonDashboard={showOnlyNonDashboard}
        handleToggleShowOnlyNonDashboard={handleToggleShowOnlyNonDashboard} // Expose toggle for dialog
        dashboardDivesiteIds={dashboardDivesiteIds}
      />
    </GlassPageContainer>
  );
};

const DivesitesDashboard = ({ systemLimit = SYSTEM_LIMIT }) => {
  return (
    <DashboardProvider>
      <DivesitesDashboardContent systemLimit={systemLimit} />
    </DashboardProvider>
  );
};

export default DivesitesDashboard;
